import { AppBar, Button, IconButton, Paper, SelectChangeEvent, TextField, Toolbar } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React, { useEffect, useState } from 'react'
import { NumericInput } from '../common/NumericInput'
import {
  filterClientName,
  filterClientAmountUp,
  filterClientAmountDown,
  filterCurrencyName,
  filterOrderType,
  filterInstrumentName,
  filterDateFrom,
  filterDateTo,
} from '../../redux/actions/filterActions'
import { useDispatch } from 'react-redux'
import { useSelector } from '../../redux/hooks/useSelector'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { openDialog } from '../../redux/actions/dialogActions'
import { MASS_ORDER_INPUT_DIALOG_ID } from '../../constants/dialogConstants'
import AdapterDateFns from '@date-io/date-fns'
import DatePicker from '@mui/lab/DatePicker'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import { newThemeObject } from '../../newTheme'
import { AddRounded } from '@mui/icons-material'
import { ColoredIconButton } from '../common/ColoredIconButton'
import { isApexUser } from '../../utils/apex'

const useStyles = makeStyles(theme => ({
  tableToolBar: {
    minHeight: 0,
    display: 'flex',
    alignContent: 'space-around',
    width: '100%',
    padding: 0,
    margin: 0,
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  textField: {
    width: '100%',
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      marginTop: 0,
      marginRight: theme.spacing(1.5),
      maxWidth: '180px',
    },
  },
  filler: {
    flexGrow: 1,
  },
  icon: {
    color: newThemeObject.palette.main.sectext,
    backgroundColor: '#ffffff',
  },
  icon2: {
    color: newThemeObject.palette.main.sectext,
    backgroundColor: '#ffffff',
    marginLeft: '5px'
  },
}))

interface OrderTableProps {
  dateFrom: Date,
  setDateFrom: (param: Date) => any,
  dateTo: Date,
  setDateTo: (param: Date) => any,
  setShowConfirmation: (param: boolean) => any
}

export const OrderTableTopBar = ({
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  setShowConfirmation,
}: OrderTableProps) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { clientId } = useParams<{ clientId: string }>()
  const { t } = useTranslation()
  const filters = useSelector(state => state.filters)
  const user = useSelector(state => state.auth.user)

  const onNameSearchFieldChange = (event: React.ChangeEvent<{ value: string }>) => {
    dispatch(filterClientName(event.target.value))
  }

  const onInstrumentSearchFieldChange = (event: React.ChangeEvent<{ value: string }>) => {
    dispatch(filterInstrumentName(event.target.value))
  }

  const onAmountUpSearchFieldChange = (event: React.ChangeEvent<{ value: string }>) => {
    dispatch(filterClientAmountUp(event.target.value))
  }

  const onAmountDownSearchFieldChange = (event: React.ChangeEvent<{ value: string }>) => {
    dispatch(filterClientAmountDown(event.target.value))
  }

  const onCurrencySearchChange = (event: React.ChangeEvent<{ value: string }>) => {
    dispatch(filterCurrencyName(event.target.value))
  }

  const onOrderTypeSearchChange = (event: React.ChangeEvent<{ value: string }>) => {
    dispatch(filterOrderType(event.target.value))
  }

  const onDateFromChange = (name: string) => (date: Date | null) => {
    setDateFrom(date || new Date())
    dispatch(filterDateFrom(date || new Date()))
  }

  const onDateToChange = (name: string) => (date: Date | null) => {
    setDateTo(date || new Date())
    dispatch(filterDateTo(date || new Date()))
  }

  useEffect(() => {
    let date = new Date()
    if ( isApexUser(user) ) {
      date.setDate(1)
      date.setMonth(0)
      date.setFullYear(2014)
    } else {
      date.setMonth(date.getMonth() - 1)
    }
    setDateFrom(date)
    dispatch(filterDateFrom(date))

    date = new Date();
    date.setDate(date.getDate() + 1)
    setDateTo(date)
    dispatch(filterDateTo(date))

    dispatch(filterClientName(''))
  }, [user])

  return (
    <AppBar position="static" style={{ background: 'rgba(0,0,0,0)' }} elevation={0}>
      <Toolbar variant="dense" className={classes.tableToolBar}>
        {!clientId && (
          <TextField
            variant="standard"
            className={classes.textField}
            placeholder={t('CLIENT')}
            onChange={onNameSearchFieldChange}
            value={filters.searchedClientName}
          />
        )}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            renderInput={params => <TextField {...params} variant="standard" className={classes.textField} />}
            inputFormat="dd/MM/yyyy"
            value={dateFrom}
            onChange={onDateFromChange('dateFromId')}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            renderInput={params => (
              <TextField {...params} variant="standard" className={classes.textField} placeholder="test" />
            )}
            inputFormat="dd/MM/yyyy"
            value={dateTo}
            onChange={onDateToChange('dateToId')}
          />
        </LocalizationProvider>

        <TextField
          variant="standard"
          className={classes.textField}
          placeholder={t('TYPE')}
          onChange={onOrderTypeSearchChange}
        />
        <NumericInput
          variant="standard"
          className={classes.textField}
          placeholder={t('AMOUNT_FROM')}
          onChange={onAmountUpSearchFieldChange}
        />
        <NumericInput
          variant="standard"
          className={classes.textField}
          placeholder={t('AMOUNT_TO')}
          onChange={onAmountDownSearchFieldChange}
        />
        <TextField
          variant="standard"
          className={classes.textField}
          placeholder={t('INSTRUMENT')}
          onChange={onInstrumentSearchFieldChange}
        />
        <TextField
          variant="standard"
          className={classes.textField}
          placeholder={t('CURRENCY')}
          onChange={onCurrencySearchChange}
        />
        {!clientId && (
          <>
            <span className={classes.filler} />

            <ColoredIconButton
              className={classes.icon2}
              onClick={() => {
                dispatch(openDialog(MASS_ORDER_INPUT_DIALOG_ID))
              }}
              size="small"
              icon={<AddRounded />}
            />
          </>
        )}
      </Toolbar>
    </AppBar>
  )
}
